import { SoraVideoSettings, SoraAudioSettings, MediaSettings, Settings } from './settings';
import { logEvent } from './logging';
import { createListenerManager } from 'infrastructure/utility/ListenerManager';

type ConnectRequest<Metadata = {}> = {
    type: 'connect';
    role: string;
    channel_id: string;
    metadata: Metadata | undefined;
    video: boolean | SoraVideoSettings;
    audio: boolean | SoraAudioSettings;
}

type OfferMessage = {
    type: 'offer';
    config: RTCConfiguration & { config: RTCConfiguration };
}

type PingMessage = {
    type: 'ping';
}

type ConnectionNotify = {
    event_type: 'connection.created' | 'connection.updated' | 'connection.destroyed';
    role: 'upstream' | 'downstream';
    channel_upstream_connections: number;
    channel_downstream_connections: number;
    audio: string;
    video: string;
}

export type Notify = ConnectionNotify;

type NotifyConnectionMessage = {
    type: 'notify';
} & Notify;

type NotifyMessage = NotifyConnectionMessage;

type Message = OfferMessage | PingMessage | NotifyMessage;

export type OfferHandler
    = ((config: RTCConfiguration, desc: RTCSessionDescriptionInit) => void)
    | ((config: RTCConfiguration, desc: RTCSessionDescriptionInit) => Promise<void>);

export type NotifyListener = (notify: Notify) => void;

export class Signaling<Metadata = {}> {
    private ws?: WebSocket;

    async start(config: Settings<Metadata>, role: string, room: string, onOffer: OfferHandler) {
        try {
            const ws = await this._openSocket(config.signalingServer, onOffer);
            this.ws = ws;

            const getMetadata = config.getMetadata;
            const metadata = getMetadata ? await new Promise<Metadata>(getMetadata) : undefined;

            this._connect(ws, role, room, config, metadata);
        } catch (err) {
            console.error('oh my', err);
            throw err;
        }
    }

    async stop() {
        if (this.ws) {
            this.ws.close();
            this.ws = undefined;
        }
    }

    sendCandidate(candidate: RTCIceCandidate) {
        const ws = this.ws;
        if (!ws) return;

        ws.send(JSON.stringify({ type: 'candidate', candidate: candidate.candidate }));
    }

    sendAnswer(desc: RTCSessionDescriptionInit) {
        const ws = this.ws;
        if (!ws) return;

        ws.send(JSON.stringify(desc));
    }

    private readonly listenerManager = createListenerManager<NotifyListener>();

    addNotifyListener(listener: NotifyListener) {
        this.listenerManager.addListener(listener);
    }

    removeNotifyListener(listener: NotifyListener) {
        this.listenerManager.removeListener(listener);
    }

    private async _openSocket(signalingServer: string, onOffer: OfferHandler) {
        const ws = new WebSocket(signalingServer);

        const willOpen = new Promise<WebSocket>(resolve => {
            ws.onopen = ev => {
                logEvent('ws is opened')(ev);
                resolve(ws);
            };
        });
        ws.onclose = logEvent('ws is closed');
        ws.onerror = logEvent('ws sent an error');

        ws.addEventListener('message', async (ev) => {
            const msg = JSON.parse(ev.data);
            this._dispatch(msg, onOffer);
        });

        return await willOpen;
    }

    private _connect(ws: WebSocket, role: string, room: string, config: MediaSettings, metadata: Metadata | undefined) {
        const video = config.video || { 'codec_type': 'VP9' };
        const audio = config.audio || false;

        const request: ConnectRequest<Metadata> = {
            type: 'connect',
            role,
            'channel_id': room,
            metadata: metadata,
            video,
            audio,
        };

        const json = JSON.stringify(request);
        ws.send(json);
    }

    private _pong() {
        const ws = this.ws;
        if (!ws) return;

        ws.send(JSON.stringify({ type: 'pong' }));
    }

    private _dispatch(msg: Message, onOffer: OfferHandler) {
        switch (msg.type) {
            case 'offer':
                onOffer(msg.config, msg);
                break;

            case 'ping':
                this._pong();
                break;

            case 'notify':
                this._dispatchNotify(msg);
                break;

            default:
                console.error('unexpected message from sora', msg);
        }
    }

    private _dispatchNotify(msg: NotifyMessage) {
        switch (msg.event_type) {
            case 'connection.created':
            case 'connection.updated':
            case 'connection.destroyed':
                [...this.listenerManager.listeners].forEach(listener => {
                    listener(msg);
                });
                break;

            default:
                console.warn('unsupported notify message from sora', msg);
        }
    }
}
